import React, { type ReactElement, type DetailedHTMLProps, type ButtonHTMLAttributes, forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import { SupText } from "../../../../components/UI/Text";
import type { CommonColors } from "../../../../types/colors";

export interface InputButtonProps extends DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
  color?: CommonColors;
  fluid?: boolean;
  placeholder?: string;
  prefixIcon?: ReactElement;
  suffixIcon?: ReactElement;
}

export const borderColors = {
  primary: "border-0.5 border-neutral-500 enabled:hover:border-neutral-700 enabled:focus:border-neutral-500 enabled:active:border-neutral-800",
  success: "border-0.5 border-success-900",
  info: "border-0.5 border-info-800",
  error: "border-0.5 border-error-925",
  warning: "border-0.5 border-warning-900",
} as const;

export const InputButton = forwardRef<HTMLButtonElement, InputButtonProps>(function InputButtonComponent(
  { fluid = false, color = "primary", placeholder, className, children, prefixIcon, suffixIcon, ...props },
  ref
) {
  return (
    <button
      tabIndex={0}
      type="button"
      ref={ref}
      className={twMerge(
        "sup-input-button inline-flex items-center p-4 w-96 max-w-full h-[3.25rem] outline-0 transition-colors duration-200 enabled:bg-neutral-50 enabled:hover:bg-neutral-75 enabled:focus:bg-neutral-50 enabled:active:bg-neutral-75 disabled:bg-neutral-50",
        fluid && "w-full",
        borderColors[color],
        className
      )}
      {...props}
    >
      {prefixIcon && <span className="mr-2">{prefixIcon}</span>}
      {placeholder && !children && (
        <SupText as="span" variant="caption" className="text-neutral-600">
          {placeholder}
        </SupText>
      )}
      {children && (
        <SupText as="span" variant="subtitle1" weight="regular" className="text-neutral-900">
          {children}
        </SupText>
      )}
      {suffixIcon && <span className="ml-auto">{suffixIcon}</span>}
    </button>
  );
});
